// src/Login.js
// ============================================================
// ScholarSync AI — Login Page
// ============================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from './config';
import { useAuth } from './AuthContext';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname;
  const { login, user, isAuthenticated, studentId, loading: authLoading } = useAuth();

  // Auto-redirect if already authenticated — role-aware
    useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else if (user?.role === 'parent') {
        navigate('/parent/dashboard', { replace: true });
      } else if (studentId) {
        navigate(`/dashboard/${studentId}`, { replace: true });
      } else {
        navigate('/profile/new', { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, studentId, user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Login failed.');
        setLoading(false);
        return;
      }

// Store token + user in context.
      // login() triggers validateAndFetchProfile() in AuthContext,
      // which sets studentId. The useEffect above handles the redirect.
      login(data.token, data.user);
    } catch (err) {
      setError('Network error — please check your connection.');
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.logo}>🎓</div>
        <h1 style={styles.title}>Your Scholarship Finder</h1>
        <p style={styles.subtitle}>Sign in to your account</p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={styles.input}
              placeholder="you@example.com"
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={styles.input}
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
  <a href="/forgot-password" style={{ color: '#4a6cf7', fontSize: 14, textDecoration: 'none' }}>
    Forgot your password?
  </a>
</div>
        </form>

        <p style={styles.switchText}>
          Don't have an account?{' '}
                              <Link to="/" style={styles.link}>Sign up here</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0D4F6B 0%, #14B8A6 100%)',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    textAlign: 'center',
  },
  logo: { fontSize: '48px', marginBottom: '8px' },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#0D4F6B',
    margin: '0 0 4px 0',
    fontFamily: 'Cambria, Georgia, serif',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0 0 24px 0',
  },
  form: { textAlign: 'left' },
  fieldGroup: { marginBottom: '16px' },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
    background: '#0D4F6B',
    border: 'none',
    borderRadius: '8px',
    marginTop: '8px',
  },
  error: {
    background: '#FEF2F2',
    color: '#DC2626',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px',
    textAlign: 'left',
  },
  switchText: {
    fontSize: '13px',
    color: '#6B7280',
    marginTop: '20px',
  },
  link: {
    color: '#14B8A6',
    fontWeight: '600',
    textDecoration: 'none',
  },
};
